import * as React from 'react';
import RaisedButton from 'material-ui/RaisedButton';
import Paper from 'material-ui/Paper';
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderColumn,
  TableRow,
  TableRowColumn
} from 'material-ui/Table';
import Phone from 'react-phone-number-input';
import { FormattedNumber, FormattedTime } from 'react-intl';
import { AuthState } from '../auth/auth.types';
import { AdminState } from '../admin/admin.types';
import { PosState } from './Types';
import { BusinessState } from '../business/business.types';
import { PosRegistrationResponse } from './Types';
import './pos.css';
import { createNoCacheExtraImageUrl } from '../util/image.util';
import {
  MASTERCARD,
  VISA
} from '../payments/Constants';
import {
  Icon_Visa,
  Icon_MasterCard
} from 'material-ui-credit-card-icons';
import CommonFeatures from '../commons/CommonFeatures';
import Snackbar from 'material-ui/Snackbar';

type Props = {
  readonly authState: AuthState
  readonly adminState: AdminState
  readonly posState: PosState
  readonly businessState: BusinessState
  readonly toAdminDashboard: () => void
  readonly toPosSale: () => void
  readonly toPosHistory: () => void
  readonly emailReceipt: (entityId: number) => Promise<Response>
};

type States = {
  readonly posSale: PosRegistrationResponse
  readonly businessLogoUrl: string
  readonly shouldPrint: boolean
  readonly printing: boolean
  readonly emailSent: boolean
};

class PosSaleResultComponent extends React.Component<Props, States> {

  constructor(props: Props) {
    super(props);

    this.state = {
      posSale: props.posState.sale,
      businessLogoUrl: props.businessState.business && props.businessState.business.picUrl ?
        props.businessState.business.picUrl :
        '',
      printing: false,
      shouldPrint: false,
      emailSent: false
    };
  }

  componentWillReceiveProps(nextProps: Props) {
    if (this.props !== nextProps) {
      this.setState({
        posSale: nextProps.posState.sale,
        businessLogoUrl: nextProps.businessState.business && nextProps.businessState.business.picUrl ?
        nextProps.businessState.business.picUrl :
        ''
      });
    }
  }

  componentWillUnmount() {
    this.setState({printing: false, shouldPrint: false});
  }

  render() {

    if (this.state.printing) {
      setTimeout(this.printReceipt, 500);
    }

    if (!this.state.printing && this.state.shouldPrint) {
      this.setState({shouldPrint: false});
    }

    return(
      <div>
        <div id="react-no-print">
          <CommonFeatures
            authState={this.props.authState}
            businessState={this.props.businessState}
          >
            {this.renderReceipt(false)}
          </CommonFeatures>
        </div>
        <div id="print-mount">
          {this.state.shouldPrint ? this.renderReceipt(true) : undefined}
        </div>
      </div>
    );
  }

  private renderReceipt = (forPrinting: boolean): JSX.Element => {
    const { posSale, businessLogoUrl, shouldPrint } = this.state;
    const { businessState } = this.props;
    const business = businessState.business;

    return(
      <div className="row justify-content-center">
        <div className={forPrinting && shouldPrint ? 'col-12' : 'col-xl-8 col-md-12 col-sm-12'}>
          <Paper className="posReceiptPaper" zDepth={3}>
            <div className="row posReceiptRow">
              <div className="col-6">
                <div className="row">
                  <div className="col-12">
                    <img height="60" src={this.getBusinessLogoUrl(businessLogoUrl)}/>
                  </div>
                </div>
              </div>
              <div className="col-6">
                <div className="row">
                  <div className="col-12 text-right">
                    <div>
                      {
                        posSale.paymentMethodType === 'CASH' ?
                          <FormattedTime
                            value={new Date(parseInt(posSale.receiptId, 10))}
                            day="numeric"
                            month="long"
                            year="numeric"
                            hour="numeric"
                            minute="numeric"
                            second="numeric"
                            timeZoneName="short"
                          /> :
                          <FormattedTime
                            value={posSale.transDate + ' ' + posSale.transTime}
                            day="numeric"
                            month="long"
                            year="numeric"
                            hour="numeric"
                            minute="numeric"
                            second="numeric"
                            timeZoneName="short"
                          />
                      }
                    </div>
                  </div>
                  <div className="col-12 text-right">
                    <div>
                      <span className="font-weight-bold">Receipt ID: </span> 
                      {posSale.receiptId}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="row posReceiptRow">
              <div className="col-6">
                <div className="row no-gutters">
                  <div className="col-12"><h6 className="font-weight-bold">Payment Method:</h6></div>
                  <div className="col-1">
                    {this.renderPaymentMethodTypeIcon()}
                  </div>
                  <div className="col-11">
                    {this.renderPaymentMethodType()}
                    &nbsp; 
                  </div>
                  <div className="col-12">{posSale.maskedPan}</div>
                </div>
              </div>
            </div>
            <div className="row posReceiptRow">
              <div className="col-12">
                You sent a payment of <FormattedNumber
                  value={posSale.total}
                  style="currency"
                  currency="USD"
                /> to {business.name}
              </div>
            </div>
            <div className="row posReceiptRow">
              <div className="col-6">
                <div className="row">
                  <div className="col-12"><h5 className="font-weight-bold">Merchant:</h5></div>
                  <div className="col-12">{business.name}</div>
                  <div className="col-12">{business.address}</div>
                  <div className="col-12">{business.city}, {business.province} {business.postalCode}</div>
                  <div className="col-12">
                    <Phone
                      countries={[business.country]}
                      value={business.phone}
                      convertToNational={true}
                      inputStyle={{ backgroundColor: '#fff', borderBottom: '0px'}}
                    />
                  </div>
                </div>
              </div>
              <div className="col-6">
                <div className="row">
                  <div className="col-12"><h5 className="font-weight-bold">Transaction Type:</h5></div>
                  <div className="col-12">{posSale.transactionType}</div>
                </div>
              </div>
            </div>
            <div className="row posReceiptRow">
              <div className="col-12">
                <Table
                  fixedHeader={true}
                  fixedFooter={false}
                  selectable={false}
                  multiSelectable={false}
                  style={{fontSize: 10}}
                >
                  <TableHeader
                    displaySelectAll={false}
                    adjustForCheckbox={false}
                    enableSelectAll={false}
                  >
                    <TableRow>
                      <TableHeaderColumn tooltip="Description" colSpan={4}>Description</TableHeaderColumn>
                      <TableHeaderColumn tooltip="Amount">Amount</TableHeaderColumn>
                    </TableRow>
                  </TableHeader>
                  <TableBody
                    displayRowCheckbox={false}
                  >
                    <TableRow>
                      <TableRowColumn colSpan={4}>{posSale.description}</TableRowColumn>
                      <TableRowColumn>
                        <FormattedNumber value={posSale.amount} style="currency" currency={posSale.currency} />
                      </TableRowColumn>
                    </TableRow>
                    <TableRow>
                      <TableRowColumn colSpan={4}>tax</TableRowColumn>
                      <TableRowColumn>
                        <FormattedNumber value={posSale.tax} style="currency" currency={posSale.currency} />
                      </TableRowColumn>
                    </TableRow>
                    <TableRow>
                      <TableRowColumn colSpan={4}>total</TableRowColumn>
                      <TableRowColumn>
                        <FormattedNumber value={posSale.total} style="currency" currency={posSale.currency} />
                      </TableRowColumn>
                    </TableRow>
                    <TableRow />
                  </TableBody>
                </Table>
              </div>
            </div>
            <div className="row posReceiptRow">
              <div className="col-auto">
                Charge will appear on your credit card statement as "<b>MIMOSA SOLUTIONS</b>"
              </div>
            </div>
            <div id="react-no-print" className="row posReceiptBtnRow">
              <div className="col-12">
                <RaisedButton
                  label="Email"
                  primary={true}
                  className="posReceiptBtn float-right"
                  disabled={!posSale.email}
                  onClick={() => this.props.emailReceipt(this.state.posSale.id)
                    .then(response => this.setState({emailSent: true}))}
                />
                <RaisedButton
                  label="Print"
                  primary={true}
                  className="posReceiptBtn float-right"
                  onClick={() => this.setState({printing: true, shouldPrint: true})}
                />
                <RaisedButton
                  label="History"
                  primary={true}
                  className="posReceiptBtn float-right"
                  onClick={this.props.toPosHistory}
                />
                <RaisedButton
                  label="New TXN"
                  primary={true}
                  className="posReceiptBtn float-right"
                  onClick={this.props.toPosSale}
                />
              </div>
            </div>
          </Paper>
        </div>
        <Snackbar
          open={this.state.emailSent}
          message="The receipt is sent to the email."
          autoHideDuration={3000}
          onRequestClose={this.handleRequestClose}
        />
      </div>
    );
  }

  private handleRequestClose = () => this.setState({emailSent: false});

  private renderPaymentMethodTypeIcon = (): JSX.Element => {
    if (this.props.posState.sale.paymentMethodType === 'CREDITCARD') {
      return this.state.posSale.cardType === 'M' ?
        <Icon_MasterCard style={{position: 'absolute', bottom: 0}} /> :
        <Icon_Visa style={{position: 'absolute', bottom: 0}} />;
    } else if (this.props.posState.sale.paymentMethodType === 'CASH') {
      return(undefined);
    } else {
      return(undefined);
    }
  }

  private renderPaymentMethodType = (): string => {
    if (this.props.posState.sale.paymentMethodType === 'CREDITCARD') {
      return this.state.posSale.cardType === 'M' ? MASTERCARD : VISA;
    } else if (this.props.posState.sale.paymentMethodType === 'CASH') {
      return 'Cash';
    } else {
      return undefined;
    }
  }

  private printReceipt = (): void => {
    this.setState(
      {printing: false},
      () => window.print()
    );
  }

  private getBusinessLogoUrl = (businessLogoUrl: string): string =>
    businessLogoUrl ? businessLogoUrl + createNoCacheExtraImageUrl(businessLogoUrl) : null
}

export default PosSaleResultComponent;



// WEBPACK FOOTER //
// ./src/pos/PosSaleResultComponent.tsx